import React from 'react';
import {
    FiTarget, FiInbox, FiFilter, FiAward, FiActivity, FiShield,
    FiLock, FiUsers, FiClipboard, FiArrowRight, FiArrowUpRight, FiGlobe, FiServer,
} from 'react-icons/fi';
import Navbar from '../components/Navbar';
import PageHero from '../components/fx/PageHero';
import Reveal from '../components/fx/Reveal';
import CascadeGrid from '../components/fx/CascadeGrid';
import Contact from '../components/Contact';
import Footer from '../components/Footer';

const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

/* ── How it works ── */
const STEPS = [
    { icon: <FiTarget size={22} />, title: 'Define scope & rewards', desc: 'Set your in-scope assets, rules of engagement, and a severity-based reward or points table. Public or invite-only.' },
    { icon: <FiInbox size={22} />, title: 'Researchers submit', desc: 'Vetted hunters report vulnerabilities through a structured form with CVSS, reproduction steps, and evidence.' },
    { icon: <FiFilter size={22} />, title: 'We triage & validate', desc: 'Our team validates, de-duplicates, and prioritises each finding on a Kanban board — with a full audit trail.' },
    { icon: <FiAward size={22} />, title: 'You fix & reward', desc: 'Track remediation to resolution, then reward researchers. Every action is logged and reportable.' },
];

/* ── Platform capabilities ── */
const CAPS = [
    { icon: <FiInbox size={22} />, title: 'Managed intake & triage', desc: 'Structured vulnerability submissions with markdown, screenshots, and file evidence.', points: ['Web-form intake', 'Duplicate detection', 'Assignee & status workflow'] },
    { icon: <FiActivity size={22} />, title: 'CVSS scoring', desc: 'Built-in CVSS calculator derives severity automatically from the vector.', points: ['CVSS 4.0 & 3.1', 'Severity-based points', 'Consistent risk rating'] },
    { icon: <FiClipboard size={22} />, title: 'Kanban triage board', desc: 'Drag reports across the workflow from New to Resolved with a clear queue.', points: ['New → Triaging → Resolved', 'Per-program filtering', 'Real-time counts'] },
    { icon: <FiAward size={22} />, title: 'Points & Hall of Fame', desc: 'Reputation points reward researchers and drive a public leaderboard.', points: ['Public Hall of Fame', 'Researcher profiles', 'Program leaderboards'] },
    { icon: <FiUsers size={22} />, title: 'Roles & audit log', desc: 'Role-based access for admins, triagers, and hunters — everything recorded.', points: ['Admin / triager / hunter', 'Full audit trail', 'Retention & archiving'] },
    { icon: <FiShield size={22} />, title: 'Security built in', desc: 'Hardened auth so your program and its data stay protected.', points: ['2FA (TOTP) & captcha', 'Rate limiting & lockout', 'Encrypted secrets at rest'] },
];

/* ── Deployment models ── */
const MODELS = [
    { icon: <FiServer size={22} />, title: 'Self-hosted', desc: 'Runs on your infrastructure or ours. Your data never leaves your control.' },
    { icon: <FiLock size={22} />, title: 'Private program', desc: 'Invite a curated set of researchers to test sensitive systems under NDA.' },
    { icon: <FiGlobe size={22} />, title: 'Public VDP', desc: 'Open a Vulnerability Disclosure Program with Safe Harbor for good-faith research.' },
];

const BugBountyPage = () => {
    return (
        <>
            <Navbar />

            <PageHero
                pill="MANAGED BUG BOUNTY"
                title={[
                    { text: 'Bug Bounty' },
                    { text: 'as a Service', gradient: true },
                ]}
                desc="Run a private or public bug bounty program on our self-hosted platform. We handle intake, triage, and rewards end to end — so your team only sees validated, prioritised findings."
                actions={[
                    { label: 'Talk to us', primary: true, icon: <FiArrowRight />, to: '#contact', onClick: scrollTo('contact') },
                    { label: 'How it works', icon: <FiTarget />, to: '#how', onClick: scrollTo('how') },
                ]}
            />

            {/* ── How it works ── */}
            <section id="how" className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-dots opacity-25 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <span className="pill tag inline-flex mb-4">HOW IT WORKS</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            From scope to <span className="text-gradient-red">resolution</span>
                        </h2>
                        <p className="text-gray-400 max-w-2xl mx-auto mt-3 text-lg">
                            A managed pipeline that turns raw researcher reports into fixes — without drowning your team.
                        </p>
                    </Reveal>

                    <CascadeGrid className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" stagger={110}>
                        {STEPS.map((s, i) => (
                            <div key={i} className="card p-6 h-full group relative">
                                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-red-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="flex items-center justify-between mb-4">
                                    <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400">
                                        {s.icon}
                                    </div>
                                    <span className="font-display text-3xl font-bold text-white/10">0{i + 1}</span>
                                </div>
                                <h3 className="font-display text-lg font-semibold mb-2">{s.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">{s.desc}</p>
                            </div>
                        ))}
                    </CascadeGrid>
                </div>
            </section>

            {/* ── Platform capabilities ── */}
            <section id="capabilities" className="relative bg-[#070810] text-white px-6 md:px-16 py-24 overflow-hidden border-y border-white/5">
                <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[800px] h-[800px] rounded-full bg-red-900/20 blur-3xl pointer-events-none" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <span className="pill tag inline-flex mb-4">PLATFORM</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            Everything a program <span className="text-gradient-red">needs</span>
                        </h2>
                        <p className="text-gray-400 max-w-2xl mx-auto mt-3 text-lg">
                            Purpose-built for vulnerability disclosure — not a repurposed ticket tracker.
                        </p>
                    </Reveal>

                    <CascadeGrid className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" stagger={100}>
                        {CAPS.map((c, i) => (
                            <div key={i} className="card p-6 h-full group relative">
                                <div className="absolute inset-x-6 top-0 h-px bg-gradient-to-r from-transparent via-red-500/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                                <div className="w-12 h-12 rounded-xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-4">
                                    {c.icon}
                                </div>
                                <h3 className="font-display text-lg font-semibold mb-2">{c.title}</h3>
                                <p className="text-gray-400 text-sm mb-4 leading-relaxed">{c.desc}</p>
                                <ul className="text-sm text-gray-300 space-y-2">
                                    {c.points.map((p, j) => (
                                        <li key={j} className="flex items-start gap-2">
                                            <span className="text-red-500 mt-0.5">▸</span>
                                            <span>{p}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </CascadeGrid>
                </div>
            </section>

            {/* ── Deployment models ── */}
            <section className="relative bg-[#05060a] text-white px-6 md:px-16 py-24 overflow-hidden">
                <div className="absolute inset-0 bg-dots opacity-25 pointer-events-none" />
                <div className="relative max-w-7xl mx-auto">
                    <Reveal className="text-center mb-14">
                        <span className="pill tag inline-flex mb-4">DEPLOYMENT</span>
                        <h2 className="font-display text-4xl md:text-5xl font-bold">
                            Your program, <span className="text-gradient-red">your way</span>
                        </h2>
                    </Reveal>
                    <CascadeGrid className="grid gap-6 md:grid-cols-3" stagger={120}>
                        {MODELS.map((m, i) => (
                            <div key={i} className="card p-8 h-full text-center group">
                                <div className="w-14 h-14 mx-auto rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center text-red-400 mb-5">
                                    {m.icon}
                                </div>
                                <h3 className="font-display text-xl font-semibold mb-2">{m.title}</h3>
                                <p className="text-gray-400 text-sm leading-relaxed">{m.desc}</p>
                            </div>
                        ))}
                    </CascadeGrid>
                </div>
            </section>

            {/* ── CTA band ── */}
            <section className="relative bg-[#070810] text-white px-6 md:px-16 py-20 overflow-hidden border-t border-white/5">
                <div className="absolute -bottom-24 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-red-900/25 blur-3xl pointer-events-none" />
                <Reveal className="relative max-w-3xl mx-auto text-center">
                    <h2 className="font-display text-3xl md:text-4xl font-bold mb-4">
                        Ready to launch your <span className="text-gradient-red">bug bounty</span>?
                    </h2>
                    <p className="text-gray-400 text-lg mb-8">
                        Tell us about your assets and we will get a managed program running — private or public.
                    </p>
                    <div className="flex flex-wrap justify-center gap-3">
                        <a href="#contact" onClick={scrollTo('contact')} className="btn-primary cursor-pointer">
                            Start a program <FiArrowRight />
                        </a>
                        <a href="#capabilities" onClick={scrollTo('capabilities')} className="btn-ghost cursor-pointer">
                            See capabilities <FiArrowUpRight />
                        </a>
                    </div>
                </Reveal>
            </section>

            <Contact />
            <Footer />
        </>
    );
};

export default BugBountyPage;
